// helpers.ts — small render-time utilities for the object-card bundle. Faithful TS
// mirrors of the PHP helpers options/tables/table_item_object_inc.php uses inline
// (make_number_pretty, get_item_id_name via the referenced-names map, etc.). Bucket
// reads go through ../shared/array-types; render-time primitives (iconMap / escapeHtml /
// ucwordsAndSpace / BASE_URL) are window globals (see globals.d.ts).

import type { ReferencedNames } from './types';

type ObjectCardHelperWindow = Window & {
    ftItemNameById?: (id: string) => string;
};

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export function isUuid(s: unknown): boolean {
    return typeof s === 'string' && UUID_RE.test(s);
}

// PHP make_number_pretty: comma thousands; 2 decimals only on a non-zero fractional part.
export function makeNumberPretty(x: unknown): string {
    const n = Number(x);
    if (x === null || x === undefined || x === '' || isNaN(n) || !isFinite(n)) return String(x ?? '');
    const hasFraction = Math.abs(n - Math.round(n)) > 0.000001;
    return hasFraction
        ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : Math.round(n).toLocaleString('en-US');
}

// PHP get_item_id_name($uuid): the display name for a referenced UUID. The client gets it
// from the referenced-names map the server emitted (no DB). When the id is NOT in that map —
// e.g. a catalogized composition/material (the natural-armor "Flesh" item_composition) whose def
// lives in its OWN catalog entry, not nested in this blob, so buildNamesMapFromBlob never saw it —
// resolve it from the session catalog (ftItemNameById, memoized) before falling back to the raw id
// (which otherwise renders as an ugly bare-UUID badge, e.g. "F4175703-…").
export function nameOf(names: ReferencedNames, uuid: unknown): string {
    const id = String(uuid ?? '');
    if (id === '') return '';
    const hit = names[id] ?? names[id.toUpperCase()];
    if (hit && hit.item_name) return hit.item_name;
    const ftItemNameById = (window as ObjectCardHelperWindow).ftItemNameById;
    if (typeof ftItemNameById === 'function') {
        const nm = ftItemNameById(id);
        if (nm) return nm;
    }
    return id;
}

// Build a SiteConfig::ROOT_URL() . 'item.php?id=<uuid>' link (the card's recurring pattern).
export function itemHref(id: unknown): string {
    return BASE_URL + 'item.php?id=' + escapeHtml(String(id ?? ''));
}

// JS-string-literal escape for values interpolated into onclick="" handlers (single-quoted).
export function jsAttr(s: unknown): string {
    return escapeHtml(String(s ?? '')); // UUIDs/ids only; escapeHtml already neutralizes quotes
}
